import {
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import { PanelResizeHandle } from 'react-resizable-panels';
import { ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import {
  LINE_SECTION_SHARE,
  lineSectionSize,
  loadOrdersShare,
  ordersShareOf,
  panelSetOf,
  panelSizes,
  pxToPercent,
  saveOrdersShare,
  scalePairSizes,
  type BoardSectionsState,
} from '../utils/boardSections';

const HEADER_PX = 28;
// The Line section's floor fits its header and four rows of facts.
const MIN_OPEN_PX = { orders: 120, detail: 160, line: 156 };
type PanelSection = keyof typeof MIN_OPEN_PX;
const PANEL_CLASS = 'flex flex-col overflow-hidden';
// A 1px rule with a taller invisible hit area; the library marks hover/drag.
const HANDLE_CLASS =
  "relative h-px shrink-0 bg-border outline-none transition-colors after:absolute after:inset-x-0 after:-top-1 after:h-3 after:content-[''] data-[resize-handle-state=hover]:bg-ring data-[resize-handle-state=drag]:bg-ring focus-visible:bg-ring data-[resize-handle-state=inactive]:cursor-default";

function useElementHeight(ref: RefObject<HTMLElement | null>): number {
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const observer = new ResizeObserver(() => setHeight(el.clientHeight));
    observer.observe(el);
    setHeight(el.clientHeight);
    return () => observer.disconnect();
  }, [ref]);
  return height;
}

/**
 * Drives a collapsible panel from the section state: collapse()/expand() are
 * applied whenever the flag, the container size or the panel set changes, and
 * retried on the next frame while the group has not laid the panel out yet.
 */
function usePanelCollapsed(
  ref: RefObject<ImperativePanelHandle | null>,
  collapsed: boolean,
  containerPx: number,
  panelSet: string,
  expandToSize: number,
): void {
  useEffect(() => {
    let frame = 0;
    let attempts = 0;
    const apply = () => {
      const panel = ref.current;
      if (panel === null) return;
      try {
        if (collapsed && !panel.isCollapsed()) panel.collapse();
        // resize, not expand(): expand() returns to the size the panel had
        // before it folded, which is its minimum when it mounted folded.
        if (!collapsed && panel.isCollapsed()) panel.resize(expandToSize);
      } catch {
        // The group has not registered this panel's layout yet.
        attempts += 1;
        if (attempts < 10) frame = requestAnimationFrame(apply);
      }
    };
    apply();
    return () => cancelAnimationFrame(frame);
  }, [ref, collapsed, containerPx, panelSet, expandToSize]);
}

interface BuyerBoardLayoutProps {
  state: BoardSectionsState;
  onSectionCollapsed: (section: PanelSection, collapsed: boolean) => void;
  /** Auto-height collapsible block above the resizable panels. */
  filters: ReactNode;
  /** Section node: header + body, filling its panel. */
  orders: ReactNode;
  /** Null when no order is selected: the section is not shown. */
  detail: ReactNode | null;
  /** Null when no line is selected (or no order is open): not shown. */
  line?: ReactNode | null;
  /**
   * Height Orders keeps when folded. Defaults to its header; the page passes
   * more when the folded section still shows the selected row.
   */
  ordersCollapsedPx?: number;
}

/** One panel handle per section, each folded and unfolded from the state. */
function useSectionPanels(
  state: BoardSectionsState,
  height: number,
  panelSet: string,
  ordersShare: number,
  hasLine: boolean,
) {
  const ordersRef = useRef<ImperativePanelHandle>(null);
  const detailRef = useRef<ImperativePanelHandle>(null);
  const lineRef = useRef<ImperativePanelHandle>(null);
  // An open Line section keeps its share: Orders and Detail expand into the rest.
  const expandTo = scalePairSizes(
    { orders: ordersShare, detail: 100 - ordersShare },
    hasLine ? LINE_SECTION_SHARE : 0,
  );
  usePanelCollapsed(ordersRef, state.orders, height, panelSet, expandTo.orders);
  usePanelCollapsed(detailRef, state.detail, height, panelSet, expandTo.detail);
  usePanelCollapsed(lineRef, state.line, height, panelSet, LINE_SECTION_SHARE);
  return { ordersRef, detailRef, lineRef };
}

interface LinePanelProps {
  panelRef: RefObject<ImperativePanelHandle | null>;
  handleDisabled: boolean;
  collapsedSize: number;
  minSize: number;
  defaultSize: number;
  onCollapsed: (collapsed: boolean) => void;
  children: ReactNode;
}

/** The Line section's panel, under Detail, with the handle above it. */
function LinePanel({
  panelRef,
  handleDisabled,
  collapsedSize,
  minSize,
  defaultSize,
  onCollapsed,
  children,
}: LinePanelProps) {
  return (
    <>
      <PanelResizeHandle disabled={handleDisabled} className={HANDLE_CLASS} />
      <ResizablePanel
        id="line"
        order={3}
        ref={panelRef}
        collapsible
        collapsedSize={collapsedSize}
        minSize={minSize}
        defaultSize={defaultSize}
        onCollapse={() => onCollapsed(true)}
        onExpand={() => onCollapsed(false)}
        className={PANEL_CLASS}
      >
        {children}
      </ResizablePanel>
    </>
  );
}

/**
 * Filters sit above at natural height; Orders and Detail share a vertical
 * resizable group. A folded panel shrinks to its 28px header. The reducer is
 * the source of truth: the group is remounted when Detail appears or goes
 * away and starts from sizes derived from the state (no restored layout).
 */
export function BuyerBoardLayout({
  state,
  onSectionCollapsed,
  filters,
  orders,
  detail,
  line = null,
  ordersCollapsedPx = HEADER_PX,
}: BuyerBoardLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const height = useElementHeight(containerRef);
  const hasDetail = detail !== null;
  const hasLine = hasDetail && line !== null;
  const panelSet = panelSetOf(hasDetail, hasLine);
  const collapsedSize = pxToPercent(HEADER_PX, height, 4);
  const ordersCollapsedSize = pxToPercent(ordersCollapsedPx, height, 10);
  const [ordersShare] = useState(loadOrdersShare);
  const { ordersRef, detailRef, lineRef } = useSectionPanels(
    state,
    height,
    panelSet,
    ordersShare,
    hasLine,
  );
  const lineSize = lineSectionSize(state, hasLine, collapsedSize);
  const sizes = scalePairSizes(
    panelSizes(
      state,
      hasDetail,
      collapsedSize,
      ordersShare,
      ordersCollapsedSize,
    ),
    lineSize,
  );
  const persistSplit = (layout: number[]) => {
    const share = ordersShareOf(
      layout.slice(0, 2),
      Math.max(collapsedSize, ordersCollapsedSize),
    );
    if (share !== null) saveOrdersShare(share);
  };
  const minSize = (section: PanelSection) =>
    Math.max(
      (section === 'orders' ? ordersCollapsedSize : collapsedSize) + 1,
      pxToPercent(MIN_OPEN_PX[section], height, 12),
    );

  return (
    <div
      ref={containerRef}
      className="flex h-[calc(100dvh-9rem)] min-h-[28rem] flex-col overflow-hidden rounded-md border border-border"
    >
      <div className="max-h-[45%] shrink-0 overflow-auto">{filters}</div>
      <div className="min-h-0 flex-1">
        <ResizablePanelGroup
          direction="vertical"
          key={panelSet}
          onLayout={persistSplit}
        >
          <ResizablePanel
            id="orders"
            order={1}
            ref={ordersRef}
            collapsible
            collapsedSize={ordersCollapsedSize}
            minSize={minSize('orders')}
            defaultSize={sizes.orders}
            onCollapse={() => onSectionCollapsed('orders', true)}
            onExpand={() => onSectionCollapsed('orders', false)}
            className={PANEL_CLASS}
          >
            {orders}
          </ResizablePanel>
          {hasDetail && (
            <>
              <PanelResizeHandle
                disabled={state.orders || state.detail}
                className={HANDLE_CLASS}
              />
              <ResizablePanel
                id="detail"
                order={2}
                ref={detailRef}
                collapsible
                collapsedSize={collapsedSize}
                minSize={minSize('detail')}
                defaultSize={sizes.detail}
                onCollapse={() => onSectionCollapsed('detail', true)}
                onExpand={() => onSectionCollapsed('detail', false)}
                className={PANEL_CLASS}
              >
                {detail}
              </ResizablePanel>
            </>
          )}
          {hasLine && (
            <LinePanel
              panelRef={lineRef}
              handleDisabled={state.detail || state.line}
              collapsedSize={collapsedSize}
              minSize={minSize('line')}
              defaultSize={lineSize}
              onCollapsed={(collapsed) => onSectionCollapsed('line', collapsed)}
            >
              {line}
            </LinePanel>
          )}
        </ResizablePanelGroup>
      </div>
    </div>
  );
}
